import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DiscoveryRadio } from '@/mocks/discovery';
import { useAudioPlayer } from '@/context/AudioContext';
import { radioStations } from '@/mocks/radios';
import Swal from 'sweetalert2';

interface DiscoveryCardProps {
  radio: DiscoveryRadio;
  sectionId: string;
}

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

const activityBadge: Record<string, { label: string; color: string }> = {
  muy_activa: { label: 'Muy Activa', color: 'bg-emerald-500' },
  activa: { label: 'Activa', color: 'bg-accent-500' },
  moderada: { label: 'Moderada', color: 'bg-amber-500' },
  pausada: { label: 'Pausada', color: 'bg-foreground-400' },
};

export default function DiscoveryCard({ radio, sectionId }: DiscoveryCardProps) {
  const navigate = useNavigate();
  const { play, isPlaying, currentStation } = useAudioPlayer();
  const [isFollowed, setIsFollowed] = useState(false);

  const radioPath = `/radio/${radio.frequency.replace('.', '-')}`;
  const isThisPlaying = isPlaying && currentStation && currentStation.frequency === radio.frequency;

  const handleEnter = useCallback(() => {
    navigate(radioPath);
  }, [navigate, radioPath]);

  const handlePlay = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const station = radioStations.find((s) => s.frequency === radio.frequency);
    if (station) {
      play(station);
    }
  }, [play, radio.frequency]);

  const handleFollow = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFollowed((prev) => !prev);
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: isFollowed ? `Dejaste de seguir a ${radio.name}` : `Ahora seguís a ${radio.name}`,
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  }, [isFollowed, radio.name]);

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}${radioPath}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: radio.name, text: `${radio.name} · ${radio.frequency}`, url });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Enlace copiado',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
      });
    }
  }, [radio.name, radio.frequency, radioPath]);

  const renderSectionMetric = () => {
    switch (sectionId) {
      case 'nuevas':
        return (
          <div className="flex items-center gap-1">
            <i className="ri-calendar-line text-[10px] text-emerald-500"></i>
            <span className="text-[10px] font-semibold text-emerald-600 whitespace-nowrap">
              {radio.createdAt ? new Date(radio.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : 'Nueva'}
            </span>
          </div>
        );
      case 'tendencia':
        return (
          <div className="flex items-center gap-1">
            <i className={`text-[10px] ${(radio.trendChange ?? 0) >= 0 ? 'text-emerald-500 ri-arrow-up-line' : 'text-red-500 ri-arrow-down-line'}`}></i>
            <span className={`text-[10px] font-bold whitespace-nowrap ${(radio.trendChange ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {radio.trendingScore ?? '-'} pts
            </span>
          </div>
        );
      case 'cerca-de-subir':
        return (
          <div className="flex items-center gap-1">
            <i className="ri-line-chart-line text-[10px] text-sky-500"></i>
            <span className="text-[10px] font-bold text-sky-600 whitespace-nowrap">
              {radio.growthPercent ?? '—'}
            </span>
          </div>
        );
      case 'mas-activas':
        return (
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${activityBadge[radio.activityLevel ?? 'moderada']?.color ?? 'bg-foreground-400'}`}></span>
            <span className="text-[10px] font-semibold text-foreground-600 whitespace-nowrap">
              {activityBadge[radio.activityLevel ?? 'moderada']?.label ?? '—'}
            </span>
          </div>
        );
      case 'mejor-score':
        return (
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-[10px] text-amber-500"></i>
            <span className="text-[10px] font-bold text-amber-600 whitespace-nowrap">
              {radio.radioScore}/100
            </span>
          </div>
        );
      case 'recomendadas':
        return (
          <div className="flex items-center gap-1">
            <i className="ri-thumb-up-fill text-[10px] text-violet-500"></i>
            <span className="text-[10px] font-semibold text-violet-600 whitespace-nowrap">
              {radio.listeners >= 5000 ? 'Popular' : 'Descubrimiento'}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      onClick={handleEnter}
      className="group bg-background-50 rounded-2xl border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer flex flex-col overflow-hidden"
    >
      <div className="p-4 md:p-5 flex items-start gap-3">
        <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden bg-background-100 shrink-0 flex items-center justify-center border border-background-200">
          <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" loading="lazy" />
          {radio.isLive && (
            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 border-2 border-background-50 animate-pulse"></div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <h3 className="font-semibold text-foreground-950 text-sm truncate">{radio.name}</h3>
            {radio.isLive && (
              <div className="shrink-0 flex items-center gap-1 bg-red-50 px-1.5 py-0.5 rounded-full">
                <span className="w-1 h-1 bg-red-500 rounded-full animate-pulse"></span>
                <span className="text-[9px] font-bold text-red-600">LIVE</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-foreground-500 flex-wrap">
            <span className="font-bold text-primary-600 whitespace-nowrap">{radio.frequency}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300 shrink-0"></span>
            <span className="truncate">{radio.category}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300 shrink-0"></span>
            <span className="truncate">{radio.city}</span>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-5 pb-3">
        <p className="text-[11px] text-foreground-500 leading-relaxed line-clamp-2">{radio.description}</p>
      </div>

      <div className="px-4 md:px-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <i className="ri-headphone-line text-[10px] text-foreground-400"></i>
            <span className="text-[10px] font-semibold text-foreground-700 whitespace-nowrap">{formatCount(radio.listeners)}</span>
          </div>
          <div className="flex items-center gap-0.5">
            <i className="ri-star-fill text-[10px] text-amber-500"></i>
            <span className="text-[10px] font-semibold text-foreground-700">{radio.rating}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-heart-line text-[10px] text-foreground-400"></i>
            <span className="text-[10px] font-semibold text-foreground-700 whitespace-nowrap">{formatCount(radio.followers)}</span>
          </div>
        </div>
        {renderSectionMetric()}
      </div>

      <div className="px-4 md:px-5 pb-3">
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-semibold text-foreground-400 whitespace-nowrap">Score</span>
          <div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                radio.radioScore >= 90 ? 'bg-emerald-500' : radio.radioScore >= 80 ? 'bg-accent-500' : radio.radioScore >= 70 ? 'bg-amber-500' : 'bg-foreground-400'
              }`}
              style={{ width: `${Math.min(radio.radioScore, 100)}%` }}
            ></div>
          </div>
          <span className="text-[9px] font-bold text-foreground-700 whitespace-nowrap">{radio.radioScore}</span>
        </div>
      </div>

      <div className="px-4 md:px-5 pb-4 flex items-center gap-2 mt-auto">
        <button
          onClick={handlePlay}
          className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5 ${
            isThisPlaying
              ? 'bg-accent-500 hover:bg-accent-600 text-background-50'
              : 'bg-primary-500 hover:bg-primary-600 text-background-50'
          }`}
        >
          <i className={`text-xs ${isThisPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
          {isThisPlaying ? 'Pausar' : 'Escuchar'}
        </button>
        <button
          onClick={handleFollow}
          className={`w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-all shrink-0 ${
            isFollowed
              ? 'bg-red-50 text-red-500'
              : 'bg-background-100 text-foreground-400 hover:bg-red-50 hover:text-red-500'
          }`}
          title={isFollowed ? 'Dejar de seguir' : 'Seguir'}
        >
          <i className={`text-sm ${isFollowed ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
        </button>
        <button
          onClick={handleShare}
          className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all shrink-0"
          title="Compartir"
        >
          <i className="ri-share-forward-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}